import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { timeAgo } from '../utils/timeAgo';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    fetchConversations();
    if (location.state?.partnerId) {
      selectConversation(location.state.partnerId);
    }
  }, [location.state?.partnerId]);

  const fetchConversations = async () => {
    try {
      const { data } = await api.get('/messages/conversations');
      setConversations(data);
    } catch (error) {
      toast.error('Failed to load conversations');
    }
  };

  const selectConversation = async (partnerId) => {
    setSelectedPartner(partnerId);
    try {
      const [msgsRes, partnerRes] = await Promise.all([
        api.get(`/messages/${partnerId}`),
        api.get(`/messages/partner/${partnerId}`),
      ]);
      setMessages(msgsRes.data);
      setPartnerInfo(partnerRes.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedPartner) return;

    setLoading(true);
    try {
      await api.post('/messages', {
        receiver_id: selectedPartner,
        content: messageText,
      });
      setMessageText('');
      selectConversation(selectedPartner);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="messages-container">
            <Card className="bg-white border border-slate-100 shadow-sm overflow-hidden">
              <div className="grid md:grid-cols-3 h-[600px]">
                {/* Conversations List */}
                <div className="border-r border-slate-100">
                  <div className="p-4 border-b border-slate-100">
                    <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase">
                      Messages
                    </h2>
                  </div>
                  <ScrollArea className="h-[540px]">
                    {conversations.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No conversations yet
                      </div>
                    ) : (
                      conversations.map((conv) => (
                        <div
                          key={conv.partner_id}
                          className={`p-4 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${
                            selectedPartner === conv.partner_id ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => selectConversation(conv.partner_id)}
                          data-testid="conversation-item"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={conv.partner_photo} />
                              <AvatarFallback className="bg-[#1E3A8A] text-white text-sm">
                                {getInitials(conv.partner_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-slate-900 truncate">
                                {conv.partner_name}
                              </p>
                              <p className="text-xs text-slate-500 truncate">
                                {conv.last_message.sender_id === user.id ? 'You: ' : ''}
                                {conv.last_message.content}
                              </p>
                            </div>
                            {conv.unread_count > 0 && (
                              <div className="bg-[#22C55E] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {conv.unread_count}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>

                {/* Messages Panel */}
                <div className="md:col-span-2 flex flex-col">
                  {!selectedPartner ? (
                    <div className="flex-1 flex items-center justify-center text-slate-500">
                      Select a conversation to start messaging
                    </div>
                  ) : (
                    <>
                      {/* Chat Header */}
                      {partnerInfo && (
                        <div className="p-4 border-b border-slate-100 flex items-center gap-3" data-testid="chat-header">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={partnerInfo.profile_photo} />
                            <AvatarFallback className="bg-[#1E3A8A] text-white text-sm">
                              {getInitials(partnerInfo.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-slate-900 truncate" data-testid="chat-header-name">
                              {partnerInfo.name}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {partnerInfo.current_role || 'GBA Alumni'}
                              {partnerInfo.batch ? ` • ${partnerInfo.batch}` : ''}
                            </p>
                          </div>
                        </div>
                      )}

                      <ScrollArea className="flex-1 p-4" data-testid="messages-list">
                        <div className="space-y-4">
                          {messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex flex-col ${
                                msg.sender_id === user.id ? 'items-end' : 'items-start'
                              }`}
                            >
                              <span className="text-xs text-slate-500 mb-1 px-1" data-testid="message-sender-name">
                                {msg.sender_id === user.id ? 'You' : (partnerInfo?.name || msg.sender_name)}
                              </span>
                              <div
                                className={`max-w-xs px-4 py-2 rounded-lg ${
                                  msg.sender_id === user.id
                                    ? 'bg-[#1E3A8A] text-white'
                                    : 'bg-slate-100 text-slate-900'
                                }`}
                              >
                                <p className="text-sm">{msg.content}</p>
                                <p
                                  className={`text-xs mt-1 ${
                                    msg.sender_id === user.id ? 'text-blue-100' : 'text-slate-500'
                                  }`}
                                >
                                  {timeAgo(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>

                      <div className="p-4 border-t border-slate-100">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Type a message..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                            data-testid="message-input"
                          />
                          <Button
                            onClick={sendMessage}
                            disabled={loading || !messageText.trim()}
                            className="bg-[#22C55E] hover:bg-[#16A34A]"
                            data-testid="send-message-button"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
